import {
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui";
import { cn } from "@/lib/utils";
import { RootState } from "@/redux/store";
import { useDeletePositionMutation, useGetPositionList } from "@/services";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Edit, MoreVertical, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { toast } from "@/components/Toast";

const usePositionConfig = () => {
  const [globalFilter, setGlobalFilter] = useState("");
  const [isAddEditPosition, setIsAddEditPosition] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<any>(null);
  const [isViewPosition, setIsViewPosition] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deletedRow, setDeletedRow] = useState<any>(null);
  const queryClient = useQueryClient();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );

  const { data: positionList, isLoading: isLoadingPositionList } =
    useGetPositionList({
      communityId: communityDetail?.communityId as string,
      queryParams: {
        enabled: !!communityDetail?.communityId,
      },
    });

  const onAddPosition = () => {
    setIsAddEditPosition(true);
  };

  const onEditPosition = (position: any) => {
    setSelectedPosition(position);
    setIsAddEditPosition(true);
  };

  const onViewPosition = (position: any) => {
    setSelectedPosition(position);
    setIsViewPosition(true);
  };

  // Define Table Columns
  const columns = [
    {
      accessorKey: "positionName",
      header: (
        <div>
          Position
          <br />
          Name
        </div>
      ),
      accessorFn: (row: any) => String(row.positionName),
    },
    {
      accessorKey: "approveBudgets",
      header: (
        <div>
          Approve
          <br />
          Budgets
        </div>
      ),
      cell: (row: any) => {
        return row?.row?.original?.approveBudgets ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        );
      },
    },
    {
      accessorKey: "approveContract",
      header: (
        <div>
          Approve
          <br />
          Contracts
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.approveContract ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },

    {
      accessorKey: "approveApInv",
      header: (
        <div>
          Approve Account
          <br />
          Payables Invoice
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.approveApInv ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "approveWorkOrders",
      header: (
        <div>
          Approve
          <br />
          Work Orders
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.approveWorkOrders ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "approvePenalties",
      header: (
        <div>
          Approve
          <br />
          Penalties
        </div>
      ),
      cell: (row: any) =>
        row?.row?.original?.approvePenalties ? (
          <Check className="w-4 h-4 text-green-600 stroke-[5px]" />
        ) : (
          <X className="w-4 h-4 text-red-600 stroke-[5px]" />
        ),
    },
    {
      accessorKey: "status",
      header: (
        <div className="flex items-center w-[80px] justify-center gap-2">
          Status
        </div>
      ),
      cell: (row: any) => {
        return (
          <Badge
            variant="outline"
            className={cn(
              row.row.original.endDate || !row.row.original.active
                ? "border-red-600 bg-red-100 text-red-600"
                : "border-green-600 bg-green-100 text-green-600",
              "rounded-full px-4 py-2"
            )}
          >
            {row.row.original.endDate || !row.row.original.active
              ? "Inactive"
              : "Active"}
          </Badge>
        );
      },
    },
    {
      id: "actions",
      header: "Actions",
      cell: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <MoreVertical className="w-5 h-5 cursor-pointer" />
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="bg-white border-0 shadow-md"
          >
            {!(row.row.original.endDate || !row.row.original.active) ? (
              <DropdownMenuItem
                className="flex items-center gap-2"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditPosition(row.row.original);
                }}
              >
                <Edit className="w-4 h-4" />
                Edit
              </DropdownMenuItem>
            ) : (
              <></>
            )}
            <DropdownMenuItem
              className="flex items-center gap-2"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDeletedRow(row.row.original);
                setOpenDeleteDialog(true);
              }}
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  // add position
  const { mutate: deletePosition, isPending: isDeletePositionPending } =
    useDeletePositionMutation({
      onError: (error?: any) => {
        toast.error(
          typeof error?.response?.data?.message === "string"
            ? error?.response?.data?.message
            : "Something went wrong"
        );
        return error?.response?.data?.message;
      },
      onSuccess: () => {
        toast.success("Position Deleted Successfully");
        queryClient.invalidateQueries({
          queryKey: ["bom-position", communityDetail?.communityId],
        });
        setOpenDeleteDialog(false);
      },
    });

  const onCancelAddEditPosition = () => {
    setIsViewPosition(false);
    setSelectedPosition(null);
    setIsAddEditPosition(false);
  };

  const onDeleteConfirm = () => {
    deletePosition({
      id: deletedRow.bomPositionId,
      communityId: communityDetail?.communityId
    });
  };

  const onRowClick = (row: any) => {
    onViewPosition(row.original);
  };

  return {
    globalFilter,
    setGlobalFilter,
    onAddPosition,
    columns,
    positionList,
    isLoadingPositionList,
    isAddEditPosition,
    selectedPosition,
    isViewPosition,
    onCancelAddEditPosition,
    openDeleteDialog,
    setOpenDeleteDialog,
    deletedRow,
    onDeleteConfirm,
    isDeletePositionPending,
    onRowClick,
  };
};

export default usePositionConfig;
